import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, Component, computed, effect, input, OnInit, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_SELECT_CONFIG, MatSelect } from '@angular/material/select';
import { EnpsControlComponent } from '@common/controls/enps-control/enps-control';
import { selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { optionContainsTerm } from '@src/suite/common/utils/util';
import { Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'st-combo-box',
    templateUrl: './combo-box.html',
    styleUrls: ['./combo-box.scss'],
    providers: [
        {
            provide: MAT_SELECT_CONFIG,
            useValue: { overlayPanelClass: 'select-overlay-panel' },
        },
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'st-combo-box',
    },
})
export class ComboBoxComponent extends EnpsControlComponent<string | number> implements OnInit {
    comboBoxFilter = new FormControl('');
    filteredOptions: Observable<{ id: string | number; title: string }[]>;
    filteredTimeZoneOptions: Observable<{ id: string; title: string }[]>;
    internalFormControl: FormControl<string | number> = new FormControl('');

    itemList = input<{ id: string | number; title: string }[]>([]);
    additionalItemList = input<{ id: string; title: string }[]>([]);
    addEmptyOption = input<boolean>(false);
    emptyLabel = input<string>('');
    addSeparatorAfterEmptyOption = input<boolean>(false);
    isNew = input<boolean>(false);
    shouldHaveFilter = input(true, {
        transform: coerceBooleanProperty,
    });

    internalValue = signal<string | number>('');

    matSelect = viewChild(MatSelect);

    searchLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(i18nKeys.PSTWebSearchLabel))();
    });

    get isRTL() {
        return this.globalTablesService.getRtl();
    }

    get isRTLLikeString(): string {
        return String(this.globalTablesService.getRtl());
    }

    constructor() {
        super();
        effect(
            () => {
                const val = this.value();
                this.internalValue.set(val);
            },
            { allowSignalWrites: true }
        );
    }

    ngOnInit() {
        this.filteredOptions = this.comboBoxFilter.valueChanges.pipe(
            startWith(''),
            map(value => this._filter(value || '')),
            takeUntil(this.destroyed$)
        );
        this.filteredTimeZoneOptions = this.comboBoxFilter.valueChanges.pipe(
            startWith(''),
            map(value => this._filterTimeZone(value || '')),
            takeUntil(this.destroyed$)
        );
    }

    private _filter(value: string): { id: string | number; title: string }[] {
        return this.itemList().filter(option => optionContainsTerm(option.id, value) || optionContainsTerm(option.title, value));
    }

    private _filterTimeZone(value: string): { id: string; title: string }[] {
        const filterValue = value.toLowerCase();
        return this.additionalItemList().filter(option => option?.id.toLowerCase()?.includes(filterValue) || option?.title?.includes(filterValue));
    }

    valueChanged() {
        if (this.internalValue() === ':empty:') {
            this.internalValue.set('');
        }
        this.changed.emit(this.internalValue());
    }

    openedChanged(event: boolean) {
        super.onMenuOpened();
        if (!event) {
            this.comboBoxFilter.setValue('');
        }
    }

    _syncValueIfNecessary() {
        this.internalFormControl.setValue(this.value());
    }

    _addValidatorIfNecessary() {
        this.internalFormControl.addValidators(Validators.required);
        this.internalFormControl.markAllAsTouched();
    }

    _setDisabledState(isDisabled: boolean) {
        if (isDisabled) {
            this.internalFormControl.disable();
        } else {
            this.internalFormControl.enable();
        }
    }

    onEscClick() {
        this.matSelect().focus();
    }
}
